import { createFileRoute } from '@tanstack/react-router'

import { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../components/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Search, Upload, Plus, CheckCircle, AlertCircle, X, Trash2 } from 'lucide-react';
import { apiFetch } from '../../utils/api';
import PaginationFooter from '../../components/PaginationFooter';
import CategoryDropdown from '../../components/CategoryDropdown';

interface CostCategory {
    id: string;
    name: string;
    internal_id: string;
}

interface UnitOfMeasure {
    id: string;
    name: string;
    abbreviation: string;
    unit_type: string;
    is_base_unit: boolean;
    conversion_factor?: number;
    base_unit_id?: string;
    is_active: boolean;
}

interface Product {
    id: string;
    name: string;
    commodity_code?: string;
    part_number?: string;
    cost_category_id?: string;
    cost_category?: CostCategory;
    base_unit_id?: string;
    base_unit?: UnitOfMeasure;
    base_quantity?: number;
    is_active: boolean;
    status?: 'draft' | 'active' | 'inactive';
}

interface EditForm {
    name: string;
    commodity_code: string;
    part_number: string;
    cost_category_id: string | null;
    base_unit_id: string | null;
    base_quantity: number | null;
    is_active: boolean;
}

interface ImportResult {
    created: number;
    updated: number;
    errors: number;
    error_details?: string[];
}

interface PreviewRow {
    row_number: number;
    name?: string;
    commodity_code?: string;
    part_number?: string;
    cost_category_internal_id?: string;
    cost_category_name?: string;
    action: string;
    validation_status: string;
    validation_issues: string[];
}

interface PreviewData {
    delimiter: string;
    total_rows: number;
    preview_rows: PreviewRow[];
    available_categories: CostCategory[];
}

interface Pagination {
    page: number;
    total: number;
    pages: number;
    limit: number;
}

export default function IngredientsPage() {
    const [products, setProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<CostCategory[]>([]);
    const [baseUnits, setBaseUnits] = useState<UnitOfMeasure[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [editingProduct, setEditingProduct] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<EditForm>({
        name: '',
        commodity_code: '',
        part_number: '',
        cost_category_id: null,
        base_unit_id: null,
        base_quantity: null,
        is_active: true
    });
    const [importing, setImporting] = useState<boolean>(false);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [showInactive, setShowInactive] = useState<boolean>(false);
    const [previewData, setPreviewData] = useState<PreviewData | null>(null);
    const [showPreview, setShowPreview] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        total: 0,
        pages: 0,
        limit: 20,
    });
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Modal state for creating new products
    const [showModal, setShowModal] = useState<boolean>(false);
    const [editingProductModal, setEditingProductModal] = useState<Product | null>(null);
    const [formData, setFormData] = useState<EditForm>({
        name: '',
        commodity_code: '',
        part_number: '',
        cost_category_id: null,
        base_unit_id: null,
        base_quantity: null,
        is_active: true
    });

    useEffect(() => {
        fetchProducts();
        fetchCategories();
        fetchBaseUnits();
    }, [pagination.page, pagination.limit]);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300); // 300ms delay

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch products when search term changes
    useEffect(() => {
        if (pagination.page !== 1) {
            setPagination(prev => ({ ...prev, page: 1 }));
        } else {
            fetchProducts();
        }
    }, [debouncedSearchTerm, showInactive]);

    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when filters change
        fetchProducts();
    }, [showInactive, debouncedSearchTerm]);



    const fetchProducts = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();

            // Pagination
            params.append('page', pagination.page.toString());
            params.append('limit', pagination.limit.toString());

            // Filters
            if (!showInactive) {
                params.append('is_active', 'true');
            }
            // Only show purchasable products (ingredients)
            params.append('is_purchasable', 'true');

            if (debouncedSearchTerm.trim()) {
                params.append('search', debouncedSearchTerm.trim());
            }

            const response = await apiFetch(`/api/products/?${params.toString()}`);
            if (response.ok) {
                const data = await response.json();
                setProducts(data.products);
                if (data.pagination) {
                    setPagination(prev => ({ ...prev, ...data.pagination }));
                }
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await apiFetch('/api/categories/');
            if (response.ok) {
                const data = await response.json();
                setCategories(data);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchBaseUnits = async () => {
        try {
            // Fetch ALL units, not just base units
            const response = await apiFetch('/api/units/');
            if (response.ok) {
                const data = await response.json();
                setBaseUnits(data);
            }
        } catch (error) {
            console.error('Error fetching units:', error);
        }
    };

    const handleEdit = (product: Product) => {
        setEditingProduct(product.id);
        setEditForm({
            name: product.name,
            commodity_code: product.commodity_code || '',
            part_number: product.part_number || '',
            cost_category_id: product.cost_category_id || null,
            base_unit_id: product.base_unit_id || null,
            base_quantity: product.base_quantity || null,
            is_active: product.is_active
        });
    };

    const handleSave = async () => {
        try {
            const response = await apiFetch(`/api/products/${editingProduct}`, {
                method: 'PATCH',
                body: JSON.stringify(editForm),
            });

            if (response.ok) {
                await fetchProducts();
                setEditingProduct(null);
            } else {
                const error = await response.json();
                alert(`Error: ${error.detail}`);
            }
        } catch (error) {
            console.error('Error updating product:', error);
            alert('Error updating product');
        }
    };

    const handleCancel = () => {
        setEditingProduct(null);
        setEditForm({
            name: '',
            commodity_code: '',
            part_number: '',
            cost_category_id: null,
            base_unit_id: null,
            base_quantity: null,
            is_active: true
        });
    };



    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setSelectedFile(file);
        setImportResult(null);
        setPreviewData(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await apiFetch('/api/products/import/preview', {
                method: 'POST',
                body: formData,
                headers: {}, // Don't set Content-Type for FormData
            });

            if (response.ok) {
                const result = await response.json();
                setPreviewData(result);
                setShowPreview(true);
            } else {
                const error = await response.json();
                alert(`Preview failed: ${error.detail}`);
            }
        } catch (error) {
            console.error('Error previewing CSV:', error);
            alert('Error previewing CSV');
        }
    };

    const handleConfirmImport = async () => {
        if (!selectedFile) return;

        setImporting(true);
        setImportResult(null);

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const response = await apiFetch('/api/products/import', {
                method: 'POST',
                body: formData,
                headers: {}, // Don't set Content-Type for FormData
            });

            if (response.ok) {
                const result = await response.json();
                setImportResult(result);
                await fetchProducts();
                setShowPreview(false);
                setPreviewData(null);
                setSelectedFile(null);
            } else {
                const error = await response.json();
                alert(`Import failed: ${error.detail}`);
            }
        } catch (error) {
            console.error('Error importing products:', error);
            alert('Error importing products');
        } finally {
            setImporting(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleCancelPreview = () => {
        setShowPreview(false);
        setPreviewData(null);
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Modal form handlers
    const resetForm = () => {
        setFormData({
            name: '',
            commodity_code: '',
            part_number: '',
            cost_category_id: null,
            base_unit_id: null,
            base_quantity: null,
            is_active: true
        });
        setEditingProductModal(null);
    };

    const startCreate = () => {
        resetForm();
        setShowModal(true);
    };



    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const url = editingProductModal
                ? `/api/products/${editingProductModal.id}`
                : '/api/products/';

            const method = editingProductModal ? 'PATCH' : 'POST';

            const response = await apiFetch(url, {
                method,
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                await fetchProducts();
                setShowModal(false);
                resetForm();
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Failed to save ingredient');
            }
        } catch (error) {
            console.error('Error saving ingredient:', error);
            alert('Failed to save ingredient');
        }
    };

    const deleteProduct = async (id: string, name: string) => {
        if (!confirm(`Are you sure you want to delete "${name}"? This will deactivate the ingredient but preserve its data.`)) {
            return;
        }

        try {
            const response = await apiFetch(`/api/products/${id}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                await fetchProducts();
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Failed to delete ingredient');
            }
        } catch (error) {
            console.error('Error deleting ingredient:', error);
            alert('Failed to delete ingredient');
        }
    };



    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Ingredients</h1>
                        <p className="text-muted-foreground">
                            Manage ingredients and their cost categories
                        </p>
                    </div>
                    <div className="flex gap-3 items-center">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            onChange={handleFileSelect}
                            className="hidden"
                        />
                        <Button
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={importing || showPreview}
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            {showPreview ? 'Preview Active' : 'Import CSV'}
                        </Button>
                        <Button onClick={startCreate}>
                            <Plus className="w-4 h-4 mr-2" />
                            Add Ingredient
                        </Button>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                    <div className="flex-1 max-w-md relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            type="text"
                            placeholder="Search ingredients by name, commodity code, or part number..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-8"
                        />
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                            {searchTerm !== debouncedSearchTerm && (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                            )}
                            {searchTerm && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSearchTerm('')}
                                    className="h-auto p-1"
                                >
                                    <X className="h-3 w-3" />
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-3 items-center">
                        <label className="flex items-center gap-2 text-sm whitespace-wrap">
                            <input
                                type="checkbox"
                                checked={showInactive}
                                onChange={(e) => setShowInactive(e.target.checked)}
                                className="rounded border-zinc-300"
                            />
                            Show inactive ingredients
                        </label>
                        <div className="text-sm text-muted-foreground">
                            {pagination.total > 0 && (
                                <span>
                                    Showing {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Import Result */}
                {importResult && (
                    <Alert>
                        <CheckCircle className="h-4 w-4" />
                        <AlertDescription>
                            <div className="space-y-2">
                                <div className="font-medium">Import Completed</div>
                                <div className="text-sm">
                                    Created: {importResult.created}, Updated: {importResult.updated}, Errors: {importResult.errors}
                                </div>
                                {importResult.error_details && importResult.error_details.length > 0 && (
                                    <div>
                                        <div className="text-sm font-medium text-destructive">Errors:</div>
                                        <ul className="text-sm text-destructive list-disc list-inside">
                                            {importResult.error_details.map((error, index) => (
                                                <li key={index}>{error}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </AlertDescription>
                    </Alert>
                )}

                {/* Import Preview */}
                {showPreview && previewData && (
                    <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                            <div className="space-y-4">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <div className="font-medium">Import Preview</div>
                                        <div className="text-sm">
                                            File: {selectedFile?.name} | Delimiter: {previewData.delimiter} | Total rows: {previewData.total_rows} | Showing first 10 rows
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button size="sm" onClick={handleConfirmImport} disabled={importing}>
                                            {importing ? 'Importing...' : 'Confirm Import'}
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={handleCancelPreview}>
                                            Cancel
                                        </Button>
                                    </div>
                                </div>

                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Row</TableHead>
                                                <TableHead>Name</TableHead>
                                                <TableHead>Commodity Code</TableHead>
                                                <TableHead>Part Number</TableHead>
                                                <TableHead>Category Internal ID</TableHead>
                                                <TableHead>Resolved Category</TableHead>
                                                <TableHead>Action</TableHead>
                                                <TableHead>Status</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {previewData.preview_rows.map((row) => (
                                                <TableRow key={row.row_number} className={
                                                    row.validation_status === '❌ Invalid' ? 'bg-destructive/10' :
                                                        row.validation_status === '⚠️ Warning' ? 'bg-yellow-50' : ''
                                                }>
                                                    <TableCell className="font-mono text-sm">{row.row_number}</TableCell>
                                                    <TableCell>{row.name || '-'}</TableCell>
                                                    <TableCell>{row.commodity_code || '-'}</TableCell>
                                                    <TableCell>{row.part_number || '-'}</TableCell>
                                                    <TableCell className="font-mono text-sm">{row.cost_category_internal_id || '-'}</TableCell>
                                                    <TableCell>{row.cost_category_name || '-'}</TableCell>
                                                    <TableCell>
                                                        <Badge variant={row.action === 'Create' ? 'default' : 'secondary'}>
                                                            {row.action}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex flex-col gap-1">
                                                            <span className="text-sm">{row.validation_status}</span>
                                                            {row.validation_issues.length > 0 && (
                                                                <div className="text-xs text-destructive">
                                                                    {row.validation_issues.join(', ')}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>

                                {previewData.available_categories && previewData.available_categories.length > 0 && (
                                    <div className="mt-4 p-3 bg-muted rounded border">
                                        <h4 className="text-sm font-medium mb-2">Available Category Internal IDs:</h4>
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
                                            {previewData.available_categories.map((cat) => (
                                                <div key={cat.internal_id} className="flex items-center gap-1">
                                                    <code className="bg-background px-1 rounded border">{cat.internal_id}</code>
                                                    <span className="text-muted-foreground">{cat.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </AlertDescription>
                    </Alert>
                )}

                {/* Products Table */}
                <div className="bg-white">
                    <div className="mb-4 text-sm text-muted-foreground">
                        💡 Click on any row to edit an ingredient
                    </div>
                    <div className="min-h-[600px] relative">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Commodity Code (VTSZ)</TableHead>
                                    <TableHead>Part Number</TableHead>
                                    <TableHead>Cost Category</TableHead>
                                    <TableHead>Unit</TableHead>
                                    <TableHead>Quantity</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    // Loading skeleton rows
                                    Array.from({ length: pagination.limit || 20 }).map((_, i) => (
                                        <TableRow key={`skeleton-${i}`}>
                                            <TableCell>
                                                <Skeleton className="h-4 w-32" />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton className="h-4 w-24" />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton className="h-4 w-32" />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton className="h-4 w-24" />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton className="h-4 w-16" />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton className="h-4 w-20" />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton className="h-6 w-16 rounded-full" />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton className="h-8 w-16" />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : products.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-8">
                                            <div className="flex flex-col items-center gap-2">
                                                <p className="text-muted-foreground">No ingredients found.</p>
                                                <Button
                                                    variant="outline"
                                                    onClick={startCreate}
                                                >
                                                    Create your first ingredient
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    products.map((product) => (
                                        <TableRow
                                            key={product.id}
                                            className={`${!product.is_active ? 'opacity-60 bg-muted/20' : ''
                                                } ${editingProduct !== product.id ? 'cursor-pointer hover:bg-muted/50' : ''
                                                }`}
                                            onClick={() => {
                                                if (editingProduct !== product.id) {
                                                    handleEdit(product);
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                {editingProduct === product.id ? (
                                                    <Input
                                                        value={editForm.name}
                                                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                                        className="w-full"
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                ) : (
                                                    <div>
                                                        {product.name}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {editingProduct === product.id ? (
                                                    <Input
                                                        value={editForm.commodity_code}
                                                        onChange={(e) => setEditForm({ ...editForm, commodity_code: e.target.value })}
                                                        className="w-full"
                                                        placeholder="VTSZ code"
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                ) : (
                                                    product.commodity_code || '-'
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {editingProduct === product.id ? (
                                                    <Input
                                                        value={editForm.part_number}
                                                        onChange={(e) => setEditForm({ ...editForm, part_number: e.target.value })}
                                                        className="w-full"
                                                        placeholder="Part number"
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                ) : (
                                                    product.part_number || '-'
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {editingProduct === product.id ? (
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <CategoryDropdown
                                                            selectedCategoryId={editForm.cost_category_id || null}
                                                            onCategoryChange={(categoryId) => setEditForm({
                                                                ...editForm,
                                                                cost_category_id: categoryId || null
                                                            })}
                                                            placeholder="Select category"
                                                            includeAllOption={true}
                                                        />
                                                    </div>
                                                ) : (
                                                    <span>
                                                        {product.cost_category ? product.cost_category.name : 'No Category'}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {editingProduct === product.id ? (
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <Select
                                                            value={editForm.base_unit_id || 'none'}
                                                            onValueChange={(value) => setEditForm({
                                                                ...editForm,
                                                                base_unit_id: value === 'none' ? null : value
                                                            })}
                                                        >
                                                            <SelectTrigger className="text-sm">
                                                                <SelectValue placeholder="Select unit" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="none">None</SelectItem>
                                                                {/* Group units by type */}
                                                                {['volume', 'weight', 'count', 'length'].map(unitType => {
                                                                    const unitsOfType = baseUnits.filter(unit => unit.unit_type === unitType);
                                                                    if (unitsOfType.length === 0) return null;

                                                                    return (
                                                                        <div key={unitType}>
                                                                            <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase">
                                                                                {unitType}
                                                                            </div>
                                                                            {unitsOfType.map((unit) => (
                                                                                <SelectItem key={unit.id} value={unit.id}>
                                                                                    {unit.name} ({unit.abbreviation})

                                                                                </SelectItem>
                                                                            ))}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                ) : (
                                                    <span>
                                                        {product.base_unit ? `${product.base_unit.name} (${product.base_unit.abbreviation})` : '-'}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {editingProduct === product.id ? (
                                                    <Input
                                                        type="number"
                                                        step="0.1"
                                                        value={editForm.base_quantity || ''}
                                                        onChange={(e) => setEditForm({
                                                            ...editForm,
                                                            base_quantity: e.target.value ? parseFloat(e.target.value) : null
                                                        })}
                                                        className="w-full"
                                                        placeholder="Quantity"
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                ) : (
                                                    product.base_quantity ? product.base_quantity.toLocaleString() : '-'
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {editingProduct === product.id ? (
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <label className="flex items-center gap-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={editForm.is_active}
                                                                onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                                                                className="rounded border-zinc-300"
                                                            />
                                                            <span className="text-sm">Active</span>
                                                        </label>
                                                    </div>
                                                ) : (
                                                    <Badge variant={
                                                        product.status === 'active' ? 'default' : 'secondary'
                                                    }>
                                                        {product.status === 'active' ? 'Active' :
                                                            product.status === 'inactive' ? 'Inactive' :
                                                                product.is_active ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {editingProduct === product.id ? (
                                                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                                        <Button size="sm" onClick={handleSave}>
                                                            Save
                                                        </Button>
                                                        <Button size="sm" variant="outline" onClick={handleCancel}>
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            deleteProduct(product.id, product.name);
                                                        }}
                                                        className="text-destructive hover:text-destructive"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>

                {/* Pagination */}
                <PaginationFooter
                    pagination={pagination}
                    onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                    onPageSizeChange={(pageSize) => setPagination(prev => ({ ...prev, limit: pageSize, page: 1 }))}
                />

                {/* CSV Format Help */}
                <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        <div className="space-y-3">
                            <div>
                                <div className="font-medium mb-2">CSV Import Format</div>
                                <div className="text-sm mb-2">
                                    Your CSV file should have the following columns:
                                </div>
                                <ul className="text-sm list-disc list-inside space-y-1">
                                    <li><strong>name</strong> (required) - Ingredient name</li>
                                    <li><strong>commodity_code</strong> (optional) - VTSZ commodity code</li>
                                    <li><strong>part_number</strong> (optional) - Manufacturer part number</li>
                                    <li><strong>cost_category_internal_id</strong> (optional) - Cost category internal ID (must match existing category internal ID)</li>
                                </ul>
                            </div>
                            <div className="space-y-2">
                                <div className="text-sm">
                                    <strong>Supported delimiters:</strong> The system automatically detects comma (,), semicolon (;), tab, or pipe (|) delimiters.
                                </div>
                                <div className="text-sm">
                                    <strong>Import process:</strong> Select a CSV file to see a preview of the first 10 rows with validation results before confirming the import.
                                </div>
                            </div>
                        </div>
                    </AlertDescription>
                </Alert>

                {/* Create/Edit Modal */}
                <Dialog open={showModal} onClose={() => setShowModal(false)} className="">
                    <DialogTitle className="">
                        {editingProductModal ? 'Edit Ingredient' : 'Create Ingredient'}
                    </DialogTitle>
                    <DialogDescription className="">
                        {editingProductModal
                            ? 'Update the ingredient information below.'
                            : 'Add a new ingredient to the system.'
                        }
                    </DialogDescription>
                    <DialogBody className="">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Name *</Label>
                                <Input
                                    id="name"
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                    placeholder="e.g., Coffee Beans, Office Supplies"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="commodity_code">Commodity Code (VTSZ)</Label>
                                <Input
                                    id="commodity_code"
                                    type="text"
                                    value={formData.commodity_code}
                                    onChange={(e) => setFormData({ ...formData, commodity_code: e.target.value })}
                                    placeholder="VTSZ commodity code"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="part_number">Part Number</Label>
                                <Input
                                    id="part_number"
                                    type="text"
                                    value={formData.part_number}
                                    onChange={(e) => setFormData({ ...formData, part_number: e.target.value })}
                                    placeholder="Manufacturer part number"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="cost_category">Cost Category</Label>
                                <CategoryDropdown
                                    selectedCategoryId={formData.cost_category_id || null}
                                    onCategoryChange={(categoryId) => setFormData({
                                        ...formData,
                                        cost_category_id: categoryId || null
                                    })}
                                    placeholder="Select category"
                                    includeAllOption={true}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="base_unit">Base Unit</Label>
                                <Select
                                    value={formData.base_unit_id || 'none'}
                                    onValueChange={(value) => setFormData({
                                        ...formData,
                                        base_unit_id: value === 'none' ? null : value
                                    })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select base unit" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        {/* Group units by type */}
                                        {['volume', 'weight', 'count', 'length'].map(unitType => {
                                            const unitsOfType = baseUnits.filter(unit => unit.unit_type === unitType);
                                            if (unitsOfType.length === 0) return null;

                                            return (
                                                <div key={unitType}>
                                                    <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase">
                                                        {unitType}
                                                    </div>
                                                    {unitsOfType.map((unit) => (
                                                        <SelectItem key={unit.id} value={unit.id}>
                                                            {unit.name} ({unit.abbreviation})
                                                            {unit.conversion_factor && ` - ${unit.conversion_factor}x`}
                                                            {unit.is_base_unit && ' (base)'}
                                                        </SelectItem>
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="base_quantity">Base Quantity</Label>
                                <Input
                                    id="base_quantity"
                                    type="number"
                                    step="0.001"
                                    value={formData.base_quantity || ''}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        base_quantity: e.target.value ? parseFloat(e.target.value) : null
                                    })}
                                    placeholder="Quantity in base units"
                                />
                            </div>

                            <div>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                        className="rounded border-zinc-300"
                                    />
                                    <span className="text-sm">Active</span>
                                </label>
                            </div>
                        </form>
                    </DialogBody>
                    <DialogActions className="">
                        <Button variant="outline" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSubmit}>
                            {editingProductModal ? 'Update' : 'Create'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/ingredients')({
    component: IngredientsPage,
})
